import { useState, useCallback } from "react";
import { Sparkles, Send, X, Loader2, Lightbulb, BookOpen, Shield, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAITutor, type ModuleTutorData } from "@/hooks/useAITutor";

interface Message {
  role: "user" | "ai";
  content: ModuleTutorData | string;
}

interface Props {
  moduleId: string;
  moduleContext?: string;
  onClose: () => void;
}

const QUICK_PROMPTS = [
  { label: "What should I focus on?", icon: "🎯" },
  { label: "Explain this choice", icon: "💡" },
  { label: "What's the risk?", icon: "⚠️" },
  { label: "Give me a hint", icon: "💬" },
  { label: "What does this number mean?", icon: "🔢" },
];

export default function AIModuleTutorDrawer({ moduleId, moduleContext, onClose }: Props) {
  const { loading, getModuleTutorHelp } = useAITutor();
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [showHistory, setShowHistory] = useState(true);

  const handleAsk = useCallback(async (q?: string) => {
    const text = (q || question).trim();
    if (!text || loading) return;

    setMessages(prev => [...prev, { role: "user", content: text }]);
    setQuestion("");

    const result = await getModuleTutorHelp({
      moduleId,
      moduleContext,
      learnerQuestion: text,
    });

    if (result.fallback || !result.data) {
      setMessages(prev => [...prev, {
        role: "ai",
        content: {
          guidance: "I'm having trouble connecting right now. Try thinking about the tradeoffs between each option — what's the short-term vs long-term impact?",
          strategic_hint: "Consider what would happen if things don't go as planned.",
          concept_explanation: "Financial decisions often involve balancing risk against potential reward.",
          caution_message: "Take your time with this decision.",
        },
      }]);
    } else {
      setMessages(prev => [...prev, { role: "ai", content: result.data! }]);
    }
  }, [question, loading, moduleId, moduleContext, getModuleTutorHelp]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full sm:max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl max-h-[85vh] flex flex-col shadow-xl animate-slide-up z-10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border/50 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-secondary" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-sm text-foreground">AI Coach</h3>
              <p className="text-[10px] text-muted-foreground">Strategic guidance • won't solve it for you</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
          {messages.length === 0 && !loading && (
            <div className="text-center py-6 space-y-3">
              <Shield className="w-8 h-8 text-secondary/40 mx-auto" />
              <p className="text-xs text-muted-foreground">
                Ask your AI Coach for strategic guidance. I'll help you think through decisions without giving away the answer.
              </p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={msg.role === "user" ? "flex justify-end" : ""}>
              {msg.role === "user" ? (
                <div className="bg-primary/10 border border-primary/20 rounded-xl rounded-br-sm px-3 py-2 max-w-[85%]">
                  <p className="text-sm text-foreground">{msg.content as string}</p>
                </div>
              ) : (
                <div className="space-y-2 animate-fade-in">
                  <div className="bg-card border border-secondary/15 rounded-xl p-3 space-y-2">
                    <p className="text-sm text-foreground leading-relaxed">
                      {(msg.content as ModuleTutorData).guidance}
                    </p>
                    <div className="bg-secondary/5 border border-secondary/10 rounded-lg p-2.5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Lightbulb className="w-3 h-3 text-secondary" />
                        <span className="text-[10px] font-semibold text-secondary">Strategic Hint</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {(msg.content as ModuleTutorData).strategic_hint}
                      </p>
                    </div>
                    <div className="bg-primary/5 border border-primary/10 rounded-lg p-2.5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <BookOpen className="w-3 h-3 text-primary" />
                        <span className="text-[10px] font-semibold text-primary">Concept</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {(msg.content as ModuleTutorData).concept_explanation}
                      </p>
                    </div>
                    {(msg.content as ModuleTutorData).caution_message && (
                      <p className="text-[10px] text-amber-400/80 flex items-center gap-1">
                        <span>⚠️</span> {(msg.content as ModuleTutorData).caution_message}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex items-center gap-2 py-3">
              <Loader2 className="w-4 h-4 text-secondary animate-spin" />
              <span className="text-xs text-muted-foreground">Thinking...</span>
            </div>
          )}
        </div>

        {/* Quick prompts */}
        {messages.length < 2 && !loading && (
          <div className="px-4 pb-2 flex flex-wrap gap-1.5 shrink-0">
            {QUICK_PROMPTS.map((p) => (
              <button
                key={p.label}
                onClick={() => handleAsk(p.label)}
                className="text-[10px] px-2.5 py-1.5 rounded-full border border-border bg-muted/30 text-muted-foreground hover:bg-secondary/10 hover:border-secondary/30 hover:text-secondary transition-colors"
              >
                {p.icon} {p.label}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="p-4 pt-2 border-t border-border/50 shrink-0">
          <div className="flex gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAsk()}
              placeholder="Ask your AI Coach..."
              className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-secondary/50"
            />
            <Button
              size="icon"
              variant="secondary"
              onClick={() => handleAsk()}
              disabled={!question.trim() || loading}
              className="shrink-0 h-9 w-9"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
